import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2, Timer, ShieldCheck, CalendarDays } from "lucide-react";

const MODES = ["none", "nearest", "up", "down"];
const STEPS = [1, 5, 6, 15];

export default function TimePolicyPage() {
  const [cfg, setCfg] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [users, setUsers] = useState([]);
  const [timeEntryCfg, setTimeEntryCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyApprovals, setBusyApprovals] = useState(false);
  const [busyTimeEntry, setBusyTimeEntry] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      const [r, a, u, te] = await Promise.all([
        api.get("/tenant/config/rounding"),
        api.get("/tenant/config/approvals"),
        api.get("/tenant/users?status=active"),
        api.get("/tenant/config/time-entry"),
      ]);
      setCfg(r.data);
      setApprovals(a.data);
      setUsers(u.data.items || []);
      setTimeEntryCfg(te.data);
    })();
  }, []);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.patch("/tenant/config/rounding", {
        rounding_mode: cfg.rounding_mode,
        rounding_minutes: cfg.rounding_minutes,
        grace_period_minutes: cfg.grace_period_minutes,
      });
      setCfg(data);
      toast.success("Time policy updated");
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  const saveApprovals = async () => {
    setBusyApprovals(true);
    try {
      const { data } = await api.patch("/tenant/config/approvals", {
        default_approver_user_id: approvals.default_approver_user_id,
        approval_revoke_window_days: approvals.approval_revoke_window_days,
        magic_link_ttl_days: approvals.magic_link_ttl_days,
        late_submission_grace_hours: approvals.late_submission_grace_hours,
      });
      setApprovals(data);
      toast.success("Approvals policy updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusyApprovals(false); }
  };

  const saveTimeEntryCfg = async () => {
    setBusyTimeEntry(true);
    try {
      const { data } = await api.patch("/tenant/config/time-entry", {
        allow_worker_duration_entry: !!timeEntryCfg.allow_worker_duration_entry,
        max_daily_minutes: parseInt(timeEntryCfg.max_daily_minutes, 10),
      });
      setTimeEntryCfg(data);
      toast.success("Time-entry policy updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusyTimeEntry(false); }
  };

  if (!cfg || !approvals || !timeEntryCfg) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-6" data-testid="time-policy-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Administration</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Time policy</h1>
        <p className="mt-2 text-muted-foreground">Rounding + approval settings. Workspace admins only.</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <div className="rounded-xl bg-accent p-3 text-secondary"><Timer className="h-5 w-5" /></div>
          <div>
            <CardTitle>Rounding rules</CardTitle>
            <CardDescription>
              Applied at clock-out. All timestamps are stored raw; only the derived <code>duration_minutes</code> is rounded.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Rounding mode</Label>
              <Select value={cfg.rounding_mode} onValueChange={(v) => setCfg({ ...cfg, rounding_mode: v })}>
                <SelectTrigger data-testid="policy-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Rounding step (minutes)</Label>
              <Select
                value={String(cfg.rounding_minutes)}
                onValueChange={(v) => setCfg({ ...cfg, rounding_minutes: parseInt(v, 10) })}
              >
                <SelectTrigger data-testid="policy-step"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STEPS.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Grace period (minutes, 0–30)</Label>
            <Input
              type="number"
              min={0}
              max={30}
              value={cfg.grace_period_minutes}
              onChange={(e) => setCfg({ ...cfg, grace_period_minutes: parseInt(e.target.value || "0", 10) })}
              data-testid="policy-grace"
            />
            <p className="text-xs text-muted-foreground">
              When set, punches within this window of a rounding boundary snap to the boundary — even if the mode would round the other way.
            </p>
          </div>
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
          <div className="flex justify-end">
            <Button onClick={save} disabled={busy} data-testid="policy-save">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save rounding
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-2xl" data-testid="approvals-config-card">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <div className="rounded-xl bg-accent p-3 text-secondary"><ShieldCheck className="h-5 w-5" /></div>
          <div>
            <CardTitle>Approvals</CardTitle>
            <CardDescription>
              Who reviews timesheets and how magic links behave. Individual workers can be overridden on their profile.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Default approver</Label>
            <Select
              value={approvals.default_approver_user_id || ""}
              onValueChange={(v) => setApprovals({ ...approvals, default_approver_user_id: v })}
            >
              <SelectTrigger data-testid="default-approver-select"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.full_name || u.email} <span className="ml-2 text-xs text-muted-foreground">({u.email})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Used for any worker without an explicit approver.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>Revoke window (days)</Label>
              <Input type="number" min={0} max={90} value={approvals.approval_revoke_window_days}
                onChange={(e) => setApprovals({ ...approvals, approval_revoke_window_days: parseInt(e.target.value || "0", 10) })}
                data-testid="revoke-window-input" />
            </div>
            <div className="space-y-2">
              <Label>Magic link TTL (days)</Label>
              <Input type="number" min={1} max={60} value={approvals.magic_link_ttl_days}
                onChange={(e) => setApprovals({ ...approvals, magic_link_ttl_days: parseInt(e.target.value || "1", 10) })}
                data-testid="magic-ttl-input" />
            </div>
            <div className="space-y-2">
              <Label>Late-submit grace (hrs)</Label>
              <Input type="number" min={0} max={168} value={approvals.late_submission_grace_hours}
                onChange={(e) => setApprovals({ ...approvals, late_submission_grace_hours: parseInt(e.target.value || "0", 10) })}
                data-testid="late-grace-input" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveApprovals} disabled={busyApprovals} data-testid="approvals-save">
              {busyApprovals && <Loader2 className="h-4 w-4 animate-spin" />} Save approvals
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-2xl" data-testid="time-entry-config-card">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <div className="rounded-xl bg-accent p-3 text-secondary"><CalendarDays className="h-5 w-5" /></div>
          <div>
            <CardTitle>Manual time entry (Phase 33)</CardTitle>
            <CardDescription>
              Punch mode is always available. When Date + Duration is enabled, workers
              can log past hours by date on their Clock page — handy for backfilling an
              offsite day. The daily cap applies to the sum of punch + duration rows
              on a single <code>work_date</code>.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
            <div>
              <Label className="text-sm font-medium">Allow workers to log by date + duration</Label>
              <p className="text-xs text-muted-foreground">
                Off by default. When off, the "Log past work" button is hidden on the
                worker Clock page. Admins can always create duration entries from the
                Time browser → New entry regardless of this setting.
              </p>
            </div>
            <Switch
              checked={!!timeEntryCfg.allow_worker_duration_entry}
              onCheckedChange={(v) => setTimeEntryCfg({ ...timeEntryCfg, allow_worker_duration_entry: !!v })}
              data-testid="allow-worker-duration-switch"
            />
          </div>
          <div className="space-y-2 max-w-xs">
            <Label>Max daily minutes (1–1440)</Label>
            <Input
              type="number"
              min={1}
              max={1440}
              value={timeEntryCfg.max_daily_minutes}
              onChange={(e) => setTimeEntryCfg({ ...timeEntryCfg, max_daily_minutes: parseInt(e.target.value || "960", 10) })}
              data-testid="max-daily-minutes-input"
            />
            <p className="text-xs text-muted-foreground">Default 960 (16h). Blocks typo-sized entries from reaching rate math.</p>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveTimeEntryCfg} disabled={busyTimeEntry} data-testid="time-entry-save">
              {busyTimeEntry && <Loader2 className="h-4 w-4 animate-spin" />} Save manual entry policy
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
